import { Component, ContentChild, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { TableData, TableDataColumn, TableDataColumnOrder } from '../../models/table.models';
import { Table } from 'primeng/table';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { LazyLoadEvent, SortEvent } from 'primeng/api';

@Component({
  selector: 'shared-ui-tables-simple-table',
  templateUrl: './simple-table.component.html',
  styleUrls: ['./simple-table.component.css'],
  standalone: false,
})
export class SimpleTableComponent implements OnInit, OnDestroy {
  @Input() columns = new Array<TableDataColumn>();
  @Input() data!: TableData<unknown>;
  @Input() noDataText = 'No data';
  @Input() filter?: TemplateRef<undefined>;
  @Input() columnFilters?: TemplateRef<undefined>[];
  @Input() filterFields?: string[];
  @Input() enablePagination = true;
  @Input() pageSize = 10;

  @Output() doubleClick = new EventEmitter<number>();
  @Output() dataOrdered = new EventEmitter<TableDataColumn>();
  @Output() pageChange = new EventEmitter<number>();

  @ContentChild('template') template!: TemplateRef<unknown>;
  @ContentChild('rowTemplate') rowTemplate!: TemplateRef<unknown>;
  @ContentChild('templateHeader') templateHeader!: TemplateRef<unknown>;
  @ViewChild('dt') table!: Table;

  defaultSortField!: string | undefined;
  defaultSortOrder!: number;
  selectedRow = 0;
  filterMap = new Map<string | undefined, TemplateRef<undefined>>();

  private dataOrderedDebouncer = new Subject<TableDataColumn>();
  private ngUnsubscribe = new Subject<void>();

  constructor() {}

  ngOnInit(): void {
    this.dataOrderedDebouncer.pipe(debounceTime(500), takeUntil(this.ngUnsubscribe)).subscribe((value) => this.dataOrdered.emit(value));
    this.setDefaultSorting();
    if (this.columnFilters != null && this.filterFields != null && this.columnFilters.length === this.filterFields.length) {
      for (let i = 0; i < this.columnFilters.length; i++) {
        this.filterMap.set(this.filterFields[i], this.columnFilters[i]);
      }
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  onDoubleClick(indexOnPage: number): void {
    const globalIndex = indexOnPage >= 10 ? indexOnPage - this.selectedRow : indexOnPage;
    this.doubleClick.emit(globalIndex);
  }

  get tableData(): unknown[] {
    return [...(this.data?.data ?? [])];
  }

  handlePage(event: LazyLoadEvent): void {
    let page = 1;
    if (event.first && event.rows) {
      page = event.first / event.rows + 1;
      this.selectedRow = event.first;
    }
    this.pageChange.emit(page);
  }

  handleSort(event: SortEvent): void {
    const field = event.field;
    if (!field) {
      return;
    }

    const col = this.columns.find((x) => x.renderColumn === field);
    if (!col) {
      return;
    }

    const newOrder = this.fromPrimeOrder(event.order ?? 0);
    col.sortOrder = newOrder;

    this.columns.filter((x) => x.renderColumn !== field).forEach((x) => (x.sortOrder = TableDataColumnOrder.None));

    this.dataOrderedDebouncer.next(col);
  }

  setSort(col: TableDataColumn): void {
    if (!col?.orderable) {
      return;
    }
    const matchingColumns = this.columns.filter((x) => x.renderColumn === col.renderColumn);
    if (matchingColumns.length > 0) {
      matchingColumns[0].sortOrder = col.sortOrder;
      this.columns.filter((x) => x.renderColumn !== col.renderColumn).forEach((x) => (x.sortOrder = 0));
    }
  }

  setArrowDirectionOnDefaultSortingColumn(): void {
    const defaultSortingColumn = this.columns.find((x) => x.defaultSortingColumn);
    if (!defaultSortingColumn || defaultSortingColumn.defaultSortingColumnSortOrder === undefined) {
      return;
    }

    defaultSortingColumn.sortOrder = defaultSortingColumn.defaultSortingColumnSortOrder;
    this.columns.filter((x) => !x.defaultSortingColumn).forEach((column) => (column.sortOrder = TableDataColumnOrder.None));
  }

  private setDefaultSorting(): void {
    const def = this.columns.find((c) => c.defaultSortingColumn);
    if (!def) {
      return;
    }

    def.sortOrder = def.defaultSortingColumnSortOrder ?? TableDataColumnOrder.Asc;
    this.columns.filter((c) => c !== def).forEach((c) => (c.sortOrder = TableDataColumnOrder.None));

    this.defaultSortField = def.renderColumn;
    this.defaultSortOrder = this.toPrimeOrder(def.sortOrder);

    this.dataOrdered.emit(def);
  }

  private toPrimeOrder(o: TableDataColumnOrder | undefined): number {
    if (o === TableDataColumnOrder.Asc) {
      return 1;
    }
    if (o === TableDataColumnOrder.Desc) {
      return -1;
    }
    return 0;
  }

  private fromPrimeOrder(o: number | null | undefined): TableDataColumnOrder {
    if (o === 1) {
      return TableDataColumnOrder.Asc;
    }
    if (o === -1) {
      return TableDataColumnOrder.Desc;
    }
    return TableDataColumnOrder.None;
  }
}
